import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ProfilePictureUploader from './ProfilePictureUploader';
import { fetchStudentDetails } from '../utils/fetchStudentDetails';
import { getProfilePictureUrl } from '../utils/utils';
import API_URL from '../apiConfig';

function BookingStudent({ closeModal }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [studentID, setStudentID] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedTeacherName, setSelectedTeacherName] = useState('');
  const [selectedTeacherProfile, setSelectedTeacherProfile] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [teacherSearchTerm, setTeacherSearchTerm] = useState('');
  const [profileDetails, setProfileDetails] = useState({ name: '', id: '', role: '', program: '', year_section: '' });
  const modalFileInputRef = useRef(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [modalStep, setModalStep] = useState('upload');
  const [modalSelectedFile, setModalSelectedFile] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const storedStudentID = localStorage.getItem('studentID');
    if (location.state?.studentID) {
      setStudentID(location.state.studentID);
      localStorage.setItem('studentID', location.state.studentID);
    } else if (storedStudentID) {
      setStudentID(storedStudentID);
    }
  }, [location.state?.studentID]); // Added location.state?.studentID to dependency array

  useEffect(() => {
    // Fetch teachers
    fetch(`${API_URL}/get_teachers`)
      .then(response => response.json())
      .then(data => {
        // Filter to only show active teachers
        const activeTeachers = data.filter(teacher => teacher.isActive);
        setTeachers(activeTeachers);
        if (activeTeachers.length === 0) {
          setError('No active teachers available. Please try again later.');
        }
      })
      .catch(error => {
        console.error('Error fetching teachers:', error);
        setError('Failed to load teachers. Please try again later.');
      });

    // Fetch students
    fetch(`${API_URL}/get_students`)
      .then(response => response.json())
      .then(data => {
        setStudents(data);
        if (data.length === 0) {
          // Optionally set an error or handle empty student list
          console.warn('No students found.');
        }
      })
      .catch(error => {
        console.error('Error fetching students:', error);
        // Optionally set an error specific to student fetching
        setError(prevError => prevError ? `${prevError} Failed to load students.` : 'Failed to load students. Please try again later.');
      });
  }, []); // Empty dependency array, so it runs once on mount

  return (
    <div className="p-8 bg-white rounded-lg">
      {error && (
        <div className="mb-4 text-red-500 text-sm">
          {error}
        </div>
      )}
      {/* Booking form section */}
      <div className="mb-4 relative">
        <label className="block text-gray-700 font-medium mb-1">Teacher:</label>
        <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2">
          {selectedTeacher && !teacherSearchTerm && (
            <div className="flex items-center gap-2">
              <img
                src={getProfilePictureUrl(selectedTeacherProfile, selectedTeacherName)}
                alt={selectedTeacherName}
                className="rounded-full w-6 h-6"
              />
              <span>{selectedTeacherName}</span>
              <button 
                onClick={() => {
                  setSelectedTeacher('');
                  setSelectedTeacherName('');
                  setSelectedTeacherProfile('');
                }}
                className="text-red-500"
              >
                x
              </button>
            </div>
          )}
          <input
            type="text"
            value={teacherSearchTerm}
            onChange={(e) => {
              setTeacherSearchTerm(e.target.value);
              setSelectedTeacher('');
              setSelectedTeacherName('');
              setSelectedTeacherProfile('');
            }}
            placeholder="Search by name"
            className="flex-grow focus:outline-none"
          />
        </div>
        {teacherSearchTerm && (
          <ul className="absolute z-10 bg-white border border-gray-300 rounded-lg mt-1 max-h-40 overflow-y-auto w-full shadow-md">
            {teachers
              .filter(teacher => {
                const fullName = `${teacher.firstName} ${teacher.lastName}`.toLowerCase();
                return fullName.includes(teacherSearchTerm.toLowerCase());
              })
              .map(teacher => (
                <li 
                  key={teacher.id} 
                  onClick={() => {
                    console.log('Selected teacher data:', teacher); // Debug log
                    setSelectedTeacher(teacher.id);
                    setSelectedTeacherName(`${teacher.firstName} ${teacher.lastName}`);                    // Store the profile picture information
                    const profilePic = teacher.profile_picture;
                    console.log('Setting profile picture:', profilePic); // Debug log
                    setSelectedTeacherProfile(profilePic);
                    setTeacherSearchTerm('');
                  }} 
                  className="px-3 py-2 cursor-pointer hover:bg-gray-200 flex items-center"
                >                  <img 
                    src={getProfilePictureUrl(teacher.profile_picture, `${teacher.firstName} ${teacher.lastName}`)}
                    alt={`${teacher.firstName} ${teacher.lastName}`}
                    className="rounded-full w-6 h-6 mr-1" 
                  />
                  <span>{teacher.firstName} {teacher.lastName}</span>
                </li>
              ))}
          </ul>
        )}
      </div>

      <div className="mb-4 relative">
        <label className="block text-gray-700 font-medium mb-1">Fellow Students:</label>
        <div className="flex flex-wrap items-center gap-2 border border-gray-300 rounded-lg px-3 py-2">
          {selectedStudents.map(studentId => {
            const student = students.find(s => s.id === studentId);
            return student ? (
              <div key={studentId} className="bg-blue-100 text-blue-800 px-2 py-1 rounded flex items-center">
                <img
                  src={getProfilePictureUrl(student.profile_picture, `${student.firstName} ${student.lastName}`)}
                  alt={`${student.firstName} ${student.lastName}`}
                  className="rounded-full w-6 h-6 mr-1"
                />
                <span>{student.firstName} {student.lastName}</span>
                <button 
                  onClick={() => setSelectedStudents(selectedStudents.filter(id => id !== studentId))}
                  className="ml-1 text-red-500"
                >
                  x
                </button>
              </div>
            ) : null;
          })}
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name"
            className="flex-grow min-w-[150px] focus:outline-none"
          />
        </div>
        {searchTerm && (
          <ul className="absolute z-10 bg-white border border-gray-300 rounded-lg mt-1 max-h-40 overflow-y-auto w-full shadow-md">
            {students
              .filter(student => 
                student.id !== studentID && 
                `${student.firstName} ${student.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map(student => (
                <li 
                  key={student.id} 
                  onClick={() => {
                    if (!selectedStudents.includes(student.id)) {
                      setSelectedStudents([...selectedStudents, student.id]);
                    }
                    setSearchTerm('');
                  }} 
                  className="px-3 py-2 cursor-pointer hover:bg-gray-200 flex items-center"
                >
                  <img
                    src={getProfilePictureUrl(student.profile_picture, `${student.firstName} ${student.lastName}`)}
                    alt={`${student.firstName} ${student.lastName}`}
                    className="rounded-full w-6 h-6 mr-1"
                  />
                  <span>{student.firstName} {student.lastName} ({student.program} {student.year_section})</span>
                </li>
              ))
            }
          </ul>
        )}
      </div>

      <div className="flex space-x-2">
        <button className="bg-blue-500 text-white px-4 py-2 rounded-lg">
          Request Appointment
        </button>
      </div>
    </div>
  );
}

export default BookingStudent;
